/**
 * Utilidades de ciclos D28D.
 *
 * Los ciclos viven en backend (`/api/cycles`). Esta utilidad solo
 * expone helpers de formato y un fallback estático para los componentes
 * que aún no leen del API. El listado real DEBE obtenerse vía
 * `GET /api/cycles`.
 */

// Fallback estático SOLO para SSR / primer paint si el backend no
// responde. La fuente de verdad es la BD del backend (cycles.json).
export const CYCLES_DATA = [
  { id: 1,  name: 'Ciclo 1',  startDate: '2026-12-14', label: 'Vacacional' },
  { id: 2,  name: 'Ciclo 2',  startDate: '2026-01-12', label: '' },
  { id: 3,  name: 'Ciclo 3',  startDate: '2026-02-09', label: '' },
  { id: 4,  name: 'Ciclo 4',  startDate: '2026-03-09', label: '' },
  { id: 5,  name: 'Ciclo 5',  startDate: '2026-04-06', label: '' },
  { id: 6,  name: 'Ciclo 6',  startDate: '2026-05-04', label: '' },
  { id: 7,  name: 'Ciclo 7',  startDate: '2026-06-01', label: '' },
  { id: 8,  name: 'Ciclo 8',  startDate: '2026-06-29', label: '' },
  { id: 9,  name: 'Ciclo 9',  startDate: '2026-07-27', label: '' },
  { id: 10, name: 'Ciclo 10', startDate: '2026-08-24', label: '' },
  { id: 11, name: 'Ciclo 11', startDate: '2026-09-21', label: '' },
  { id: 12, name: 'Ciclo 12', startDate: '2026-10-19', label: '' },
  { id: 13, name: 'Ciclo 13', startDate: '2026-11-16', label: '' },
];

export const getCycleByDate = () => CYCLES_DATA;

export const formatCycleDate = (dateStr) => {
  if (!dateStr) return '';
  const m = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return '';
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'long' });
};

export const toDateInputValue = (dateStr) => {
  const m = String(dateStr || '').match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : '';
};

// Calcula la fecha de fin de un ciclo D28D (28 días desde startDate).
export const computeCycleEnd = (startDate) => {
  const m = String(startDate || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (Number.isNaN(d.getTime())) return null;
  d.setDate(d.getDate() + 27);
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${mo}-${day}`;
};

/** Fin efectivo: usa endDate del ciclo si existe; si no, 28 días (D28D plataforma). */
export const resolveCycleEnd = (cycleOrStartDate) => {
  if (cycleOrStartDate && typeof cycleOrStartDate === 'object') {
    const custom = cycleOrStartDate.endDate || cycleOrStartDate.end_date;
    if (custom) return toDateInputValue(custom);
    return computeCycleEnd(cycleOrStartDate.startDate);
  }
  return computeCycleEnd(cycleOrStartDate);
};

export const cycleDurationDays = (startDate, endDate) => {
  const start = toDateInputValue(startDate);
  const end = toDateInputValue(endDate);
  if (!start || !end) return null;
  const a = new Date(`${start}T12:00:00`);
  const b = new Date(`${end}T12:00:00`);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return null;
  const diff = Math.round((b.getTime() - a.getTime()) / (24 * 60 * 60 * 1000));
  return diff >= 0 ? diff + 1 : null;
};

export const todayCycleStr = () => new Intl.DateTimeFormat('en-CA', {
  timeZone: 'America/Bogota',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
}).format(new Date());

export const isDateInCycle = (startDate, refDate = todayCycleStr(), endDate = null) => {
  const end = endDate ? toDateInputValue(endDate) : resolveCycleEnd(startDate);
  const start = toDateInputValue(startDate);
  if (!start || !end) return false;
  return refDate >= start && refDate <= end;
};

export const sortCyclesByStart = (cycles = []) => (
  [...cycles].sort((a, b) => String(a.startDate).localeCompare(String(b.startDate)))
);

/**
 * Ciclo cuyo rango incluye hoy. Si hay solapamiento, gana el de inicio más reciente.
 */
export const resolveActiveCycle = (cycles = [], refDate = todayCycleStr()) => {
  const matches = sortCyclesByStart(cycles).filter(
    (c) => isDateInCycle(c.startDate, refDate, c.endDate || c.end_date),
  );
  return matches.length ? matches[matches.length - 1] : null;
};

/** Ciclo operativo: activo hoy, próximo por fecha, o último histórico. */
export const resolveOperationalCycle = (cycles = [], refDate = todayCycleStr()) => {
  const sorted = sortCyclesByStart(cycles).filter((c) => c?.startDate);
  if (!sorted.length) return null;
  const active = resolveActiveCycle(sorted, refDate);
  if (active) return active;
  const upcoming = sorted.find((c) => toDateInputValue(c.startDate) > refDate);
  if (upcoming) return upcoming;
  return sorted[sorted.length - 1];
};

export const pickDefaultCycleId = (cycles = []) => {
  const operational = resolveOperationalCycle(cycles);
  return operational?.id != null ? String(operational.id) : '';
};

/**
 * Ciclos adquiribles: vigentes o futuros. Excluye terminados (end < hoy).
 */
export const filterPurchasableCycles = (cycles = [], refDate = todayCycleStr()) => {
  const ref = String(refDate).slice(0, 10);
  return (cycles || []).filter((c) => {
    const start = toDateInputValue(c?.startDate || c?.start_date);
    if (!start) return false;
    const end = resolveCycleEnd(c);
    if (end && end < ref) return false;
    return true;
  });
};

export const isCyclePurchasable = (cycle, refDate = todayCycleStr()) => (
  Boolean(cycle) && filterPurchasableCycles([cycle], refDate).length > 0
);

function cycleCatalogKey(c) {
  const name = String(c?.name || '').trim().toLowerCase();
  const start = toDateInputValue(c?.startDate || c?.start_date);
  if (!name) return `id:${c?.id ?? ''}|${start}`;
  return `${name}|${start}`;
}

function cycleCatalogScore(c) {
  let s = 0;
  if (c?.is_active_today) s += 8;
  if (c?.endDate || c?.end_date) s += 4;
  if (c?.gym_id == null) s += 2;
  return s;
}

/** Una fila por id; si hay clones, se queda el de fechas más completas. */
export function uniqueCyclesById(cycles = []) {
  const map = new Map();
  for (const c of cycles || []) {
    if (!c || c.id == null) continue;
    const id = String(c.id);
    const prev = map.get(id);
    if (!prev || cycleCatalogScore(c) > cycleCatalogScore(prev)) map.set(id, c);
  }
  return [...map.values()];
}

/**
 * Catálogo para asignar ciclo en Vigencias: vigentes/futuros de Maestros,
 * sin copias gym+plataforma del mismo nombre/fecha.
 */
export function cyclesForVigenciaAssign(cycles = [], refDate = todayCycleStr()) {
  const unique = uniqueCyclesById(cycles);
  const byKey = new Map();
  for (const c of unique) {
    const key = cycleCatalogKey(c);
    const prev = byKey.get(key);
    if (!prev || cycleCatalogScore(c) > cycleCatalogScore(prev)) byKey.set(key, c);
  }
  return sortCyclesByStart(filterPurchasableCycles([...byKey.values()], refDate));
}

/**
 * Valor del select en Vigencias: conserva el ciclo de la cuenta solo si sigue
 * vigente/futuro. Si venció, deja vacío (opción «Sin ciclo») — no sustituye
 * en silencio otro cycle_id.
 */
export function pickVigenciaAssignCycleId(cycles = [], seedCycleId, refDate = todayCycleStr()) {
  const options = cyclesForVigenciaAssign(cycles, refDate);
  const ids = new Set(options.map((c) => String(c.id)));
  const seed = seedCycleId != null && seedCycleId !== '' ? String(seedCycleId) : '';
  if (seed && ids.has(seed)) return seed;
  return '';
}
